import { useMediaQuery } from '@mui/material';
import React from 'react';
import { createJuvareTheme } from '../../JuvareTheme';
import { useDebounce, useSmallOrMediumDevice } from '../../hooks';
export const minDrawerWidthPixels = 200;
export const defaultDrawerWidthPixels = 600;
export const defaultDrawerWidthSmallScreen = 300;
export const maxDrawerWidthPixels = 1000;
export const useDrawerSlideout = ({ isDraggable }) => {
    const isScreenSmallerLg = useMediaQuery((theme) => theme.breakpoints.down('lg'), { noSsr: true });
    const isScreenSmallerMd = useSmallOrMediumDevice();
    const [isDrawerInFullScreen, setIsDrawerInFullScreen] = React.useState(isScreenSmallerMd);
    const [drawerWidth, setDrawerWidth] = React.useState(defaultDrawerWidthPixels);
    const debouncedDrawerWidth = useDebounce(drawerWidth, 250);
    const [isResizing, setIsResizing] = React.useState(false);
    const adjustedJuvareTheme = React.useMemo(() => createJuvareTheme((options) => (Object.assign(Object.assign({}, options), { breakpoints: {
            values: {
                xs: 0,
                sm: 600,
                md: 960 + debouncedDrawerWidth,
                lg: 1280 + debouncedDrawerWidth,
                xl: 1920 + debouncedDrawerWidth,
            },
        } }))), [debouncedDrawerWidth]);
    React.useEffect(() => {
        if (isScreenSmallerLg) {
            setDrawerWidth(defaultDrawerWidthSmallScreen);
            return;
        }
        setDrawerWidth(defaultDrawerWidthPixels);
    }, [isScreenSmallerLg]);
    React.useEffect(() => {
        if (!isScreenSmallerMd) {
            setIsDrawerInFullScreen(false);
        }
        else {
            setIsDrawerInFullScreen(true);
        }
    }, [isScreenSmallerMd]);
    const startResizing = () => {
        if (!isDraggable) {
            return;
        }
        setIsResizing(true);
        document.body.style.userSelect = 'none';
    };
    const stopResizing = () => {
        setIsResizing(false);
        document.body.style.userSelect = 'auto';
    };
    const resizeDrawer = React.useCallback((e) => {
        if (!isDraggable) {
            return;
        }
        if (isResizing) {
            const newWidth = window.innerWidth - e.clientX;
            if (newWidth > minDrawerWidthPixels && newWidth < maxDrawerWidthPixels) {
                setDrawerWidth(newWidth);
            }
        }
    }, [isDraggable, isResizing]);
    React.useEffect(() => {
        if (isResizing) {
            document.addEventListener('pointermove', resizeDrawer);
            document.addEventListener('pointerup', stopResizing);
        }
        else {
            document.removeEventListener('pointermove', resizeDrawer);
            document.removeEventListener('pointerup', stopResizing);
        }
        return () => {
            document.removeEventListener('pointermove', resizeDrawer);
            document.removeEventListener('pointerup', stopResizing);
        };
    }, [isResizing, resizeDrawer]);
    return {
        isDrawerInFullScreen,
        setIsDrawerInFullScreen,
        adjustedJuvareTheme,
        isResizing,
        setIsResizing,
        startResizing,
        drawerWidth,
        debouncedDrawerWidth,
        isDraggable,
    };
};
